/**
 * BrokeragesPage — Admin split-panel view of all brokerages + their agents.
 *
 * KOPS-513: marketing/sales data pipeline. Left panel lists every
 * brokerage in the platform with agent counts; right panel shows the
 * selected brokerage's profile + team member roster. Same navy+gold
 * split-panel pattern as ContactsPage / SubscribersPage / TeamPage.
 *
 * Route: /admin/brokerages (AISearchPortal domain branch)
 *
 * Backend:
 *   GET  /api/broker/admin/brokerages              → brokerage list
 *   GET  /api/broker/admin/brokerages/:id/members   → member roster
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faSearch,
  faUsers,
  faMapMarkerAlt,
  faPhone,
  faEnvelope,
  faIdCard,
  faCircle,
  faCrown,
  faUser,
  faCalendarAlt,
  faChartLine,
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';

const colors = {
  navy: '#0F1E35',
  navy2: '#162844',
  navy3: '#1E3557',
  gold: '#C9952A',
  gold2: '#E8B44A',
  white: '#FFFFFF',
  off: '#E8EEF6',
  offBg: '#eef1f6',
  muted: '#8FA3BF',
  green: '#27ae60',
  red: '#e74c3c',
  orange: '#f39c12',
  blue: '#3498db',
  border: '#dee2e6',
};

const fonts = {
  display: "'Bebas Neue', Impact, sans-serif",
  body: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

const FONT_LINK =
  'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';

interface Brokerage {
  id: string;
  name: string;
  office_address: string | null;
  office_phone: string | null;
  office_email: string | null;
  office_mls_id: string | null;
  office_short_id: string | null;
  license_number: string | null;
  mls_name: string | null;
  board_name: string | null;
  broker_mls_id: string | null;
  designated_broker: string | null;
  plan_type: string | null;
  plan_status: string | null;
  total_agents: number;
  active_agents: number;
  created_at: string | null;
}

interface Member {
  id: string;
  display_name: string;
  email: string;
  invite_phone: string | null;
  invite_license_number: string | null;
  invite_license_type: string | null;
  member_status: string | null;
  join_method: string | null;
  subscription_plan: string | null;
  monthly_fee: number;
  is_admin: boolean;
  created_at: string | null;
  joined_at: string | null;
  last_active: string | null;
}

const BrokeragesPage: React.FC = () => {
  const [brokerages, setBrokerages] = useState<Brokerage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Brokerage | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [search, setSearch] = useState('');

  const fetchBrokerages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/broker/admin/brokerages');
      if (res.data?.success) setBrokerages(res.data.brokerages || []);
    } catch (err) {
      console.error('Failed to load brokerages:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMembers = useCallback(async (brokerageId: string) => {
    setMembersLoading(true);
    try {
      const res = await axiosInstance.get(`/broker/admin/brokerages/${brokerageId}/members`);
      if (res.data?.success) setMembers(res.data.members || []);
    } catch {
      setMembers([]);
    } finally {
      setMembersLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBrokerages();
  }, [fetchBrokerages]);

  useEffect(() => {
    if (selected?.id) fetchMembers(selected.id);
    else setMembers([]);
  }, [selected?.id, fetchMembers]);

  const filtered = brokerages.filter(
    (b) =>
      !search ||
      b.name?.toLowerCase().includes(search.toLowerCase()) ||
      b.designated_broker?.toLowerCase().includes(search.toLowerCase()) ||
      b.office_email?.toLowerCase().includes(search.toLowerCase()) ||
      b.mls_name?.toLowerCase().includes(search.toLowerCase())
  );

  const totalAgentsAcross = brokerages.reduce((s, b) => s + b.total_agents, 0);

  const fmtDate = (d: string | null) =>
    d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  return (
    <div style={{ minHeight: '100vh', background: colors.navy, fontFamily: fonts.body }}>
      <link href={FONT_LINK} rel="stylesheet" />

      {/* Nav bar */}
      <div
        style={{
          background: colors.navy3,
          borderBottom: `2px solid ${colors.gold}`,
          padding: '12px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontFamily: fonts.display, fontSize: '20px', color: colors.white, letterSpacing: '2px' }}>
            AI<span style={{ color: colors.gold2 }}>Search</span>Portal
          </span>
          <span style={{ color: colors.muted, fontSize: '12px' }}>/ Admin / Brokerages</span>
        </div>
      </div>

      {/* Stats strip */}
      <div
        style={{
          background: colors.navy2,
          borderBottom: `1px solid ${colors.navy3}`,
          padding: '14px 24px',
          display: 'flex',
          gap: '24px',
          alignItems: 'center',
        }}
      >
        <StatChip icon={faBuilding} label="Brokerages" value={brokerages.length} color={colors.gold} />
        <StatChip icon={faUsers} label="Total Agents" value={totalAgentsAcross} color={colors.blue} />
        <StatChip icon={faChartLine} label="Active" value={brokerages.filter((b) => b.plan_status === 'active').length} color={colors.green} />
      </div>

      {/* Split panel */}
      <div style={{ display: 'flex', height: 'calc(100vh - 105px)' }}>
        {/* Left — brokerage list */}
        <div
          style={{
            width: '380px',
            flexShrink: 0,
            background: colors.navy2,
            borderRight: `1px solid ${colors.navy3}`,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${colors.navy3}` }}>
            <div style={{ position: 'relative' }}>
              <FontAwesomeIcon
                icon={faSearch}
                style={{ position: 'absolute', left: '12px', top: '10px', fontSize: '12px', color: colors.muted }}
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search brokerages…"
                style={{
                  width: '100%',
                  background: colors.navy3,
                  color: colors.white,
                  border: '1px solid transparent',
                  borderRadius: '6px',
                  padding: '8px 12px 8px 32px',
                  fontSize: '12px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: '32px', textAlign: 'center', color: colors.muted, fontSize: '12px' }}>
                Loading brokerages…
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', color: colors.muted, fontSize: '12px' }}>
                {search ? 'No matching brokerages' : 'No brokerages found'}
              </div>
            ) : (
              filtered.map((b) => {
                const isSelected = selected?.id === b.id;
                return (
                  <div
                    key={b.id}
                    onClick={() => setSelected(b)}
                    style={{
                      padding: '14px 16px',
                      cursor: 'pointer',
                      borderBottom: `1px solid ${colors.navy3}`,
                      borderLeft: isSelected ? `3px solid ${colors.gold}` : '3px solid transparent',
                      background: isSelected ? colors.navy3 : 'transparent',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: colors.white }}>{b.name}</div>
                      <span style={{ fontSize: '10px', color: colors.muted }}>
                        {b.total_agents} agent{b.total_agents !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div style={{ fontSize: '11px', color: colors.muted, marginTop: '3px' }}>
                      {b.designated_broker || b.office_email || '—'}
                      {b.mls_name ? ` · ${b.mls_name}` : ''}
                    </div>
                    {b.office_address && (
                      <div style={{ fontSize: '10px', color: colors.muted, marginTop: '2px', opacity: 0.7 }}>
                        {b.office_address.substring(0, 50)}{b.office_address.length > 50 ? '…' : ''}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right — detail */}
        <div style={{ flex: 1, overflowY: 'auto', background: colors.offBg }}>
          {selected ? (
            <div style={{ padding: '24px' }}>
              {/* Profile header */}
              <div
                style={{
                  background: '#f8f9fa',
                  borderLeft: `4px solid ${colors.gold}`,
                  borderRadius: '8px',
                  padding: '20px 24px',
                  marginBottom: '16px',
                }}
              >
                <h2
                  style={{
                    fontFamily: fonts.display,
                    fontSize: '28px',
                    color: colors.navy,
                    margin: '0 0 4px',
                    letterSpacing: '0.5px',
                  }}
                >
                  {selected.name}
                </h2>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '12px', color: colors.muted }}>
                  {selected.designated_broker && (
                    <span><FontAwesomeIcon icon={faCrown} style={{ color: colors.gold, marginRight: '4px' }} />{selected.designated_broker}</span>
                  )}
                  {selected.office_email && (
                    <span><FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '4px' }} />{selected.office_email}</span>
                  )}
                  {selected.office_phone && (
                    <span><FontAwesomeIcon icon={faPhone} style={{ marginRight: '4px' }} />{selected.office_phone}</span>
                  )}
                </div>
                {selected.office_address && (
                  <div style={{ fontSize: '12px', color: colors.muted, marginTop: '6px' }}>
                    <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: '4px' }} />{selected.office_address}
                  </div>
                )}
              </div>

              {/* Stats row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '16px' }}>
                <MiniStat label="Total Agents" value={selected.total_agents} color={colors.blue} />
                <MiniStat label="Active" value={selected.active_agents} color={colors.green} />
                <MiniStat label="MLS" value={selected.mls_name || '—'} color={colors.gold} />
                <MiniStat label="Board" value={selected.board_name || '—'} color={colors.muted} />
                <MiniStat label="License" value={selected.license_number || '—'} color={colors.navy} />
                <MiniStat label="Office ID" value={selected.office_short_id || '—'} color={colors.navy} />
              </div>

              {/* Members list */}
              <div
                style={{
                  background: colors.white,
                  border: `1px solid ${colors.border}`,
                  borderLeft: `4px solid ${colors.gold}`,
                  borderRadius: '10px',
                }}
              >
                <div
                  style={{
                    padding: '14px 20px',
                    borderBottom: `1px solid ${colors.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <h5
                    style={{
                      margin: 0,
                      fontSize: '11px',
                      fontWeight: 700,
                      color: colors.navy,
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                    }}
                  >
                    <FontAwesomeIcon icon={faUsers} style={{ color: colors.gold, marginRight: '8px' }} />
                    Team Members ({members.length})
                  </h5>
                </div>
                <div style={{ padding: '12px 16px' }}>
                  {membersLoading ? (
                    <div style={{ color: colors.muted, fontSize: '12px', padding: '16px', textAlign: 'center' }}>
                      Loading members…
                    </div>
                  ) : members.length === 0 ? (
                    <div style={{ color: colors.muted, fontSize: '12px', padding: '16px', textAlign: 'center' }}>
                      No team members found
                    </div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
                          <Th>Name</Th>
                          <Th>Email</Th>
                          <Th>License</Th>
                          <Th>Status</Th>
                          <Th>Role</Th>
                          <Th>Joined</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {members.map((m, idx) => (
                          <tr
                            key={m.id}
                            style={{
                              borderBottom: `1px solid ${colors.border}`,
                              background: idx % 2 === 0 ? 'transparent' : '#f9fafb',
                            }}
                          >
                            <Td>
                              <span style={{ fontWeight: 600, color: colors.navy }}>{m.display_name}</span>
                              {m.is_admin && (
                                <span style={{
                                  marginLeft: '6px',
                                  fontSize: '9px',
                                  background: `${colors.gold}22`,
                                  color: colors.gold,
                                  padding: '1px 5px',
                                  borderRadius: '3px',
                                  fontWeight: 700,
                                }}>
                                  ADMIN
                                </span>
                              )}
                            </Td>
                            <Td>{m.email || '—'}</Td>
                            <Td>{m.invite_license_number || '—'}</Td>
                            <Td>
                              <StatusBadge status={m.member_status} />
                            </Td>
                            <Td>{m.join_method === 'mls_sync' ? 'MLS Sync' : m.join_method || '—'}</Td>
                            <Td>{fmtDate(m.joined_at || m.created_at)}</Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                padding: '48px',
                color: colors.muted,
              }}
            >
              <FontAwesomeIcon icon={faBuilding} style={{ fontSize: '48px', marginBottom: '12px', color: colors.gold }} />
              <div style={{ fontSize: '15px', fontWeight: 600, color: colors.navy }}>Select a brokerage</div>
              <div style={{ fontSize: '12px', marginTop: '4px' }}>
                Pick a brokerage from the left to view their profile and agent roster
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---- Helpers ----

const StatChip: React.FC<{ icon: any; label: string; value: number; color: string }> = ({ icon, label, value, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <FontAwesomeIcon icon={icon} style={{ color, fontSize: '12px' }} />
    <span style={{ color: colors.white, fontWeight: 700, fontSize: '16px' }}>{value}</span>
    <span style={{ color: colors.muted, fontSize: '11px' }}>{label}</span>
  </div>
);

const MiniStat: React.FC<{ label: string; value: string | number; color: string }> = ({ label, value, color }) => (
  <div
    style={{
      background: colors.white,
      border: `1px solid ${colors.border}`,
      borderLeft: `3px solid ${color}`,
      borderRadius: '6px',
      padding: '10px 12px',
    }}
  >
    <div style={{ fontSize: '10px', color: colors.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}
    </div>
    <div style={{ fontSize: '16px', fontWeight: 700, color: colors.navy, marginTop: '2px' }}>
      {value}
    </div>
  </div>
);

const StatusBadge: React.FC<{ status: string | null }> = ({ status }) => {
  const s = (status || '').toLowerCase();
  const color = s === 'active' ? colors.green : s === 'pending' ? colors.orange : colors.muted;
  return (
    <span
      style={{
        background: `${color}22`,
        color,
        fontSize: '10px',
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: '4px',
        textTransform: 'uppercase',
      }}
    >
      {status || 'unknown'}
    </span>
  );
};

const Th: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th
    style={{
      padding: '8px 12px',
      textAlign: 'left',
      fontSize: '10px',
      fontWeight: 700,
      color: colors.muted,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    }}
  >
    {children}
  </th>
);

const Td: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <td style={{ padding: '10px 12px', color: colors.navy }}>{children}</td>
);

export default BrokeragesPage;
